import { isPouchNotFound } from '@cf-shared/data-model/pouch';

/**
 * The remembered food picks for one AI scanner page.
 *
 * When a scanner reads a food name out of a photo, its first guess is often
 * wrong, and the user corrects it by picking a food themselves. That correction
 * is remembered against the name the AI produced, so the next scan of the same
 * name matches straight to the food the user already chose.
 *
 * The meal-photo scanner and the recipe scanner each used to hold their own copy
 * of this — the same load, the same save, the same in-memory shape, differing
 * only in the `_local` document id (deferred finding df-bc08ed83dd29). The copies
 * had drifted: one reported an unexpected local-database failure and the other
 * only wrote a console line, which reaches no debugger. This shared version keeps
 * the reporting.
 *
 * A page owns one of these and calls it. It is deliberately NOT a base class and
 * NOT an injectable service: `design-docs/scanner-shared-components.md`
 * ("Decision: no ScannerPageBase") rejected inheritance for this family, and
 * `scanner-model-selection.ts` is the same move already made on these same two
 * pages. Dependencies are passed per call, exactly as that class does, so the
 * page keeps owning what it injects.
 *
 * Each scanner keeps its OWN document. A food picked for a photographed plate is
 * not obviously the food wanted for the same word in a written recipe, and
 * merging the two is a product decision nobody has taken.
 */

/** One remembered pick: the food the user chose for an AI-produced name. */
export interface ScannerFoodPreference {
  foodId: string;
  foodName: string;
  pickCount: number;
}

/** The remembered picks, keyed by the lowercased, trimmed AI name. */
export interface ScannerFoodPreferences {
  [normalizedName: string]: ScannerFoodPreference;
}

/** The part of a PouchDB handle this memory uses. */
export interface PreferenceMemoryWriteDb {
  get(docId: string): Promise<any>;
  put(doc: any): Promise<any>;
}

/** The part of `DatabaseWrapper` this memory uses. */
export interface PreferenceMemoryDb {
  getWriteDb(): PreferenceMemoryWriteDb | null;
}

/** The part of `LoggingService` this memory uses. */
export interface PreferenceMemoryReporter {
  trackSilentProblem(problemName: string, context: any): void;
}

export class ScannerPreferenceMemory {
  private preferences: ScannerFoodPreferences = {};

  /**
   * @param docId the `_local/*` PouchDB document this scanner stores its picks in
   * @param reportLabel the leading word of this scanner's Sentry report names.
   *        The recipe scanner passes 'Recipe-scan' and the meal-photo scanner
   *        passes 'Meal-scan', which keeps the recipe scanner's three report
   *        names byte-identical to what it filed before this class existed, so
   *        its existing Sentry issues keep grouping.
   */
  constructor(private readonly docId: string, private readonly reportLabel: string) {}

  /** The remembered pick for an AI name, or undefined when there is none. */
  get(normalizedName: string): ScannerFoodPreference | undefined {
    return this.preferences[normalizedName];
  }

  /**
   * Load the saved picks. Uses getWriteDb() (always local) because `_local/*`
   * docs don't replicate — a pass-through read against remote would 404.
   *
   * The loaded picks go UNDER whatever is already in memory rather than
   * replacing it: a pick the user made while this load was still running is
   * newer than the stored one, and may exist nowhere else if its own save
   * could not read the document.
   */
  async loadAsync(db: PreferenceMemoryDb, logging: PreferenceMemoryReporter) {
    try {
      const doc = await db.getWriteDb().get(this.docId);
      this.preferences = { ...readPreferencesFromDoc(doc), ...this.preferences };
    } catch (err) {
      // expected: not-found from get() when the doc doesn't exist yet — nothing stored to add.
      // anything else is an unexpected local-DB read failure: surface it.
      if (!isPouchNotFound(err)) {
        logging.trackSilentProblem(`${this.reportLabel} preferences load failed`, { message: (err as Error)?.message });
      }
    }
  }

  /**
   * Remember that the user picked this food for an AI-detected name, and persist it.
   *
   * The save merges into the stored document rather than replacing it with the
   * in-memory map. The in-memory map can be empty or partial — the load failed,
   * or has not finished yet, or another scanner page saved picks since — and
   * writing it over the stored map would silently wipe the user's whole pick
   * history (deferred finding df-79cd410ccf4e).
   */
  async recordPickAsync(
    aiName: string,
    foodId: string,
    foodName: string,
    db: PreferenceMemoryDb,
    logging: PreferenceMemoryReporter,
  ) {
    const normalizedName = aiName.toLowerCase().trim();
    const inMemory = this.preferences[normalizedName];
    this.preferences[normalizedName] = {
      foodId,
      foodName,
      pickCount: (inMemory?.pickCount || 0) + 1,
    };

    try {
      let doc: any;
      try {
        doc = await db.getWriteDb().get(this.docId);
      } catch (err) {
        if (!isPouchNotFound(err)) {
          // an unexpected local-DB read failure: surface it, and write nothing —
          // without the stored picks in hand, any write could replace them.
          // the pick stays in memory for the rest of this session.
          logging.trackSilentProblem(`${this.reportLabel} preferences read-for-save failed`, { message: (err as Error)?.message });
          return;
        }
        // expected: not-found when the doc doesn't exist yet — seed a new one.
        doc = { _id: this.docId };
      }

      // stored picks win over in-memory ones, except for the name just picked
      const stored = readPreferencesFromDoc(doc);
      const merged: ScannerFoodPreferences = { ...this.preferences, ...stored };
      merged[normalizedName] = {
        foodId,
        foodName,
        pickCount: (stored[normalizedName]?.pickCount || inMemory?.pickCount || 0) + 1,
      };

      doc.preferences = merged;
      await db.getWriteDb().put(doc);
      // spread over the current map, so a pick made while this save was awaiting is kept
      this.preferences = { ...this.preferences, ...merged };
    } catch (err) {
      // an unexpected local-DB write failure — surface it (the preference is a
      // best-effort cache, so we still continue, but a debugger must see it)
      logging.trackSilentProblem(`${this.reportLabel} preferences save failed`, { message: (err as Error)?.message });
    }
  }
}

/**
 * Read the picks out of a stored document, ignoring anything of the wrong shape.
 *
 * The document may have been written by an older build, or be damaged, so its
 * `preferences` field is not trusted: a value that is not a plain object reads as
 * no picks at all, and an entry that is not a usable pick is dropped rather than
 * handed to the matcher, which would otherwise look a food up by a missing or
 * non-string id.
 */
function readPreferencesFromDoc(doc: any): ScannerFoodPreferences {
  const stored = doc?.preferences;
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};

  const preferences: ScannerFoodPreferences = {};
  for (const normalizedName of Object.keys(stored)) {
    const entry = stored[normalizedName];
    if (!entry || typeof entry !== 'object') continue;
    if (typeof entry.foodId !== 'string' || !entry.foodId) continue;
    preferences[normalizedName] = {
      foodId: entry.foodId,
      foodName: typeof entry.foodName === 'string' ? entry.foodName : '',
      pickCount: typeof entry.pickCount === 'number' && entry.pickCount > 0 ? entry.pickCount : 1,
    };
  }
  return preferences;
}
